import {
  KubernetesAiSavingsOpportunity,
  SavingsOpportunity,
} from "@/services/cloudchipr.api";
import { AllPossibleTopOpportunitiesOptionsType } from "@/components/pages/common/opportunities/utils/types/types";
import { readDataByDotSeparatedString } from "@/components/pages/common/ag-grid/utils/helpers/readDataByDotSeparatedString";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";
import { getProviderName } from "@/utils/helpers/providers/getProviderName";
import { opportunityTypeTitlesByActionType } from "@/components/pages/savings-opportunities/utils/constants/common";
import { isKubernetesAiSavingsOpportunity } from "@/components/pages/ai-kubernetes-recommendations/utils/helpers/isKubernetesAiSavingsOpportunity";

export const generateTopOpportunities = (
  opportunities: SavingsOpportunity[] | KubernetesAiSavingsOpportunity[],
  groupBy: AllPossibleTopOpportunitiesOptionsType,
  fittingItemsCount: number = 3,
) => {
  const groupedData = groupData(opportunities, groupBy);

  return generateTopOpportunitiesFromGroups(groupedData, fittingItemsCount);
};

const groupData = (
  opportunities: SavingsOpportunity[] | KubernetesAiSavingsOpportunity[],
  groupBy: AllPossibleTopOpportunitiesOptionsType,
) => {
  return opportunities.reduce(
    (acc, opportunity) => {
      let keyName = readDataByDotSeparatedString(
        groupBy,
        opportunity,
      ) as string;
      const isAiKubernetesOpportunities =
        isKubernetesAiSavingsOpportunity(opportunity);

      if (groupBy === "service") {
        keyName = getResourceTypeName(opportunity.service, {
          type: "long",
        });
      }

      if (groupBy === "cloud_provider") {
        keyName = getProviderName(opportunity.cloud_provider, { title: true });
      }

      if (groupBy === "action_type") {
        keyName = opportunity?.action_type
          ? opportunityTypeTitlesByActionType[opportunity.action_type]
          : "";
      }

      if (
        groupBy === "account.provider_account_id" &&
        !isAiKubernetesOpportunities
      ) {
        keyName =
          opportunity.account?.provider_account_name ||
          opportunity.account?.provider_account_id;
      }

      if (groupBy === "cluster.cluster_id" && isAiKubernetesOpportunities) {
        keyName =
          (opportunity.cluster?.name || opportunity.cluster?.cluster_id) ?? "_";
      }

      acc[keyName] = (acc[keyName] ?? 0) + (opportunity.monthly_savings ?? 0);

      return acc;
    },
    {} as Record<string, number>,
  );
};

const generateTopOpportunitiesFromGroups = (
  groupedData: Record<string, number>,
  fittingItemsCount: number,
) => {
  const entries = Object.entries(groupedData).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);

  const top = entries.slice(0, fittingItemsCount).map(([key, value]) => ({
    name: key,
    value,
    percent: Math.round((value / total) * 100),
  }));
  const topPercentTotal = top.reduce((acc, item) => acc + item.percent, 0);

  return [...top, generateOthers(entries, topPercentTotal, fittingItemsCount)];
};

const generateOthers = (
  entries: [string, number][],
  topPercentTotal: number,
  fittingItemsCount: number,
) => {
  const othersEntries = entries.slice(fittingItemsCount);
  const othersValue = othersEntries.reduce((sum, [, value]) => sum + value, 0);

  return {
    name: "Others",
    value: +othersValue.toFixed(2),
    percent: 100 - topPercentTotal,
    spread: othersEntries.map(([key, value]) => ({
      name: key,
      value: +value.toFixed(2),
    })),
  };
};
